import { useHistory, useParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { postNamedCommand, postNamedQuery } from '../../../../../service'

dayjs.extend(utc)

const createUserMessage = (threadId, prompt) => ({
  threadId,
  threadMessageId: null,
  actor: 'user',
  message: prompt,
  data: {},
  isGenerating: false,
  generationStatus: null,
  responseId: null,
  attachments: [],
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

const createAgentPlaceholderMessage = (threadId, responseId) => ({
  threadId,
  threadMessageId: responseId,
  actor: 'agent',
  message: '',
  data: {},
  isGenerating: true,
  generationStatus: 'Thinking...',
  responseId: null,
  attachments: [],
  isPlaceholder: true,
  createdDate: dayjs.utc().toISOString()
})

export const abundanceDesigner = {
  useInvokeAbundanceDesigner: () => {
    const queryClient = useQueryClient()
    return useMutation({
      mutationFn: async ({ prompt, threadId }) => {
        queryClient.setQueryData(['denali/denali-abundance-designer-thread', threadId], prev => {
          if (!prev) {
            return {
              thread: {
                messages: [createUserMessage(threadId, prompt)]
              }
            }
          }
          prev.thread.messages.push(createUserMessage(threadId, prompt))

          return prev
        })
        const { data } = await postNamedCommand('denali', 'invoke-abundance-designer', {
          threadId,
          prompt
        })

        return data
      },
      onSuccess: async (data, { prompt, threadId }) => {
        const newMessage = data.message
        queryClient.setQueryData(['denali/denali-abundance-designer-thread', threadId], prev => {
          // replace placeholder message
          const placeholderIndex = prev.thread.messages.findIndex(m => m.actor === 'user' && m.isPlaceholder)
          prev.thread.messages[placeholderIndex] = newMessage
          prev.thread.messages.push(createAgentPlaceholderMessage(threadId, newMessage.responseId))

          return prev
        })
      }
    })
  },

  useAbundanceDesignerThread: (threadId, options = {}) => {
    const { enabled = true, refetchInterval = null } = options
    return useQuery({
      queryKey: ['denali/denali-abundance-designer-thread', threadId],
      queryFn: async () => {
        const { data } = await postNamedQuery('denali', 'get-abundance-designer-thread', {
          threadId
        })

        return data
      },
      enabled,
      refetchInterval,
      refetchOnWindowFocus: !!refetchInterval
    })
  },

  useGetAttachmentData: (threadId, threadMessageAttachmentId, options = {}) => {
    const { refetchInterval = null } = options
    return useQuery({
      queryKey: ['denali/get-abundance-designer-thread-attachment', threadId, threadMessageAttachmentId],
      queryFn: async () => {
        const { data } = await postNamedQuery('denali', 'get-abundance-designer-thread-attachment', {
          threadId,
          threadMessageAttachmentId
        })

        return data
      },
      enabled: !!(threadId && threadMessageAttachmentId),
      refetchInterval,
      refetchOnWindowFocus: !!refetchInterval
    })
  },

  useGenerationPolling: (message) => {
    const refetchInterval = useMemo(
      () => message?.isGenerating ? 5000 : undefined,
      [message?.isGenerating]
    )
    const queryClient = useQueryClient()

    const { mutateAsync, ...rest } = useMutation(
      async ({ message }) => {
        const { response, data } = await postNamedQuery(
          'denali',
          'get-abundance-designer-thread-message',
          {
            threadId: message.threadId,
            threadMessageId: message.threadMessageId
          }
        )

        if (response.status === 204) {
          return null
        }

        return data
      },
      {
        onSuccess (data, { message }) {
          if (!data?.message) return // case for 204
          const newMessage = data.message
          queryClient.setQueryData(
            ['denali/denali-abundance-designer-thread', message.threadId],
            prev => {
              if (!prev) return prev
              const idx = prev.thread.messages.findIndex(m => m.threadMessageId === message.threadMessageId)
              if (idx > -1) {
                const msgs = [...prev.thread.messages]
                msgs[idx] = newMessage
                return {
                  ...prev,
                  thread: {
                    ...prev.thread,
                    messages: msgs
                  }
                }
              }
              return prev
            }
          )
        }
      }
    )

    useEffect(() => {
      if (!refetchInterval || !message) return
      mutateAsync({ message }).catch(console.error)
      const id = setInterval(() => {
        mutateAsync({ message }).catch(console.error)
      }, refetchInterval)
      return () => clearInterval(id)
    }, [
      message,
      message?.threadId,
      message?.threadMessageId,
      refetchInterval,
      mutateAsync
    ])

    return { mutateAsync, ...rest }
  },

  useListOwnedThreads: (options = {}) => {
    const { enabled = true, refetchInterval = null } = options
    return useQuery({
      queryKey: ['denali/list-abundance-designer-user-threads'],
      queryFn: async () => {
        const { data } = await postNamedQuery('denali', 'list-abundance-designer-user-threads', {
        })

        return data
      },
      enabled,
      refetchInterval,
      refetchOnWindowFocus: !!refetchInterval
    })
  }
}

export const usePlaygroundApp = () => {
  const { threadId } = useParams()
  const history = useHistory()
  const { data: thread, isLoading: isThreadLoading } = abundanceDesigner.useAbundanceDesignerThread(threadId, { enabled: !!threadId })

  const { mutateAsync: invokeAbundanceDesigner } = abundanceDesigner.useInvokeAbundanceDesigner()
  const addMessage = useCallback(async (message) => {
    const response = await invokeAbundanceDesigner({
      threadId,
      prompt: message
    })

    if (!threadId) {
      history.replace(`/denali-labs/abundance-designer/playground/${response.threadId}`)
    }
  }, [invokeAbundanceDesigner, history, threadId])

  const [selectedAttachment, setSelectedAttachment] = useState(null)
  const { data: attachmentData, isFetching: attachmentDataLoading } = abundanceDesigner.useGetAttachmentData(selectedAttachment?.threadId, selectedAttachment?.threadMessageAttachmentId)

  return {
    addMessage,
    thread: thread?.thread,
    isThreadLoading: threadId && isThreadLoading,
    selectedAttachment,
    setSelectedAttachment,
    attachmentData: attachmentData?.attachment,
    attachmentDataLoading
  }
}
